type ProfileFields = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
};

type TrackPayload = Record<string, any>;

import {GraphQLResult, generateClient} from '@aws-amplify/api';
import axios, {AxiosInstance, AxiosResponse, AxiosError} from 'axios';
import {INetworkApiRequestData, INetworkRequestData} from '../model/Network';
import {clsx, type ClassValue} from 'clsx';
import {twMerge} from 'tailwind-merge';
import {toast} from 'react-toastify';
import Trace from 'more-trace';
import moment from 'moment';

// ---------- GraphQL Client ----------
export class GraphQLRequest {
  static clientInstance: any;

  static getInstance() {
    if (!GraphQLRequest.clientInstance) {
      GraphQLRequest.clientInstance = generateClient();
    }
    return GraphQLRequest.clientInstance;
  }

  static request(config: INetworkRequestData): Promise<GraphQLResult<any>> {
    return GraphQLRequest.getInstance().graphql({
      query: config.query,
      variables: config.variables,
      authToken: process.env.NEXT_PUBLIC_TOKEN,
    });
  }
}

// ---------- REST API Request Handler ----------
export class APIRequest {
  static instance: AxiosInstance;

  static getInstance(): AxiosInstance {
    if (!APIRequest.instance) {
      APIRequest.instance = axios.create({
        baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'api-key': `${process.env.NEXT_PUBLIC_API_KEY}`,
        },
      });
    }
    return APIRequest.instance;
  }

  static request(
    config: INetworkApiRequestData,
  ): Promise<{status: number; data: any}> {
    return new Promise(resolve => {
      APIRequest.getInstance()
        .request(config)
        .then((result: AxiosResponse) => {
          resolve({status: result.status, data: result.data});
        })
        .catch((error: AxiosError) => {
          resolve({
            status: error?.response?.status || 403,
            data: error?.response?.data || {
              message: 'Something went wrong',
            },
          });
        });
    });
  }
}

// ---------- Utility Functions ----------
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const showToast = (
  message: string,
  type: 'info' | 'success' | 'warning' | 'error' = 'info',
) => {
  toast[type](message, {
    position: 'top-right',
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'colored',
  });
};

export const traceTrackEvent = (eventName: string, payload: TrackPayload) => {
  if (!eventName || !payload) return;

  try {
    Trace.trackEvents(eventName, payload);
  } catch (error) {
    console.error('Event tracking failed:', error);
  }
};

export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

export const formatDate = (date: Date | string): string => {
  return moment(date).format("DD MMM 'YY, hh:mm A");
};

export function getProfileCompletionPercentage({
  firstName,
  lastName,
  email,
  phoneNumber,
}: ProfileFields): number {
  const fields = [firstName, lastName, email, phoneNumber];
  const filledFields = fields.filter(field => field && field.trim() !== '');
  return Math.round((filledFields.length / fields.length) * 100);
}
